import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { setSentryUser, clearSentryUser } from "../utils/sentry";

const UserContext = createContext(undefined);

const initialState = {
  user: null,
  loading: false,
  error: null,
};

function mapAuth0User(auth0User) {
  if (!auth0User) return null;

  return {
    id: auth0User.sub,
    email: auth0User.email,
    name: auth0User.name || auth0User.nickname,
    auth0_user_id: auth0User.sub,
  };
}

/**
 * Admin app user context — uses Auth0 profile only.
 * The member portal `/user` endpoint is not required for admin navigation.
 */
export function UserProvider({ children }) {
  const { user: auth0User, isAuthenticated, isLoading: auth0Loading } =
    useAuth0();
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (auth0Loading) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      return;
    }

    if (isAuthenticated && auth0User) {
      localStorage.setItem("auth0_user_id", auth0User.sub);

      const user = mapAuth0User(auth0User);
      setSentryUser({
        id: user.id,
        email: user.email,
        name: user.name,
        sub: user.auth0_user_id,
      });

      setState({
        user,
        loading: false,
        error: null,
      });
      return;
    }

    localStorage.removeItem("auth0_user_id");
    clearSentryUser();
    setState(initialState);
  }, [isAuthenticated, auth0User, auth0Loading]);

  const value = useMemo(
    () => ({
      ...state,
      fetchUserData: () => {},
    }),
    [state]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
